import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/client.js';
import clsx from 'clsx';

const TABS = [
  { id: 'logins',   label: 'Logins' },
  { id: 'activity', label: 'Activity' },
  { id: 'pages',    label: 'Pages' },
];

const ACTION_COLORS = {
  login:            'bg-emerald-100 text-emerald-800',
  logout:           'bg-slate-100 text-slate-600',
  login_failed:     'bg-red-100 text-red-800',
  create:           'bg-blue-100 text-blue-800',
  update:           'bg-amber-100 text-amber-800',
  delete:           'bg-red-100 text-red-800',
  win:              'bg-emerald-100 text-emerald-800',
  lose:             'bg-red-100 text-red-800',
  change_password:  'bg-purple-100 text-purple-800',
  impersonate:      'bg-orange-100 text-orange-800',
  stop_impersonate: 'bg-orange-100 text-orange-800',
};

function fmtDt(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function ActionChip({ action }) {
  return (
    <span className={clsx('text-[11px] px-1.5 py-0.5 rounded font-mono font-medium',
      ACTION_COLORS[action] || 'bg-panel text-navy')}>
      {action}
    </span>
  );
}

export default function UserActivity() {
  const [tab, setTab] = useState('logins');

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Admin</div>
        <h1 className="text-2xl">User Activity</h1>
        <div className="rule-crimson" />
      </header>

      <div className="border-b border-panelDark flex gap-1">
        {TABS.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 text-sm border-b-2 -mb-px',
              tab === t.id ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:text-crimson'
            )}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'logins'   && <LoginsTab />}
      {tab === 'activity' && <ActivityTab />}
      {tab === 'pages'    && <PagesTab />}
    </div>
  );
}

function LoginsTab() {
  const q = useQuery({
    queryKey: ['audit-log', 'logins'],
    queryFn: () => api.get('/admin/audit-log', { params: { action: 'login', limit: 500 } }).then(r => r.data.data),
  });
  const rows = q.data || [];

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted">{q.isLoading ? 'Loading…' : `${rows.length} recent logins`}</p>
      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">IP</th>
              <th className="text-left px-3 py-2">Device / Browser</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <div className="font-medium">{r.user_name || r.user_email || '—'}</div>
                  {r.user_name && <div className="text-[11px] text-muted">{r.user_email}</div>}
                </td>
                <td className="px-3 py-1.5 text-xs text-muted whitespace-nowrap">{fmtDt(r.at)}</td>
                <td className="px-3 py-1.5 text-xs font-mono text-muted">{r.ip || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-muted truncate max-w-xs" title={r.user_agent || ''}>
                  {r.user_agent ? r.user_agent.slice(0, 80) : '—'}
                </td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={4} className="text-center text-muted py-6">No login events found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PagesTab() {
  const [userId, setUserId] = useState('');

  const facets = useQuery({
    queryKey: ['audit-facets'],
    queryFn: () => api.get('/admin/audit-log/facets').then(r => r.data.data),
    staleTime: 60_000,
  });

  const q = useQuery({
    queryKey: ['page-views', userId],
    queryFn: () => {
      const params = userId ? { user_id: userId } : {};
      return api.get('/admin/page-views', { params }).then(r => r.data);
    },
  });
  const data = q.data || {};
  const topPages = data.top_pages || [];
  const recent = data.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 items-center">
        <select className="input w-auto" value={userId} onChange={e => setUserId(e.target.value)}>
          <option value="">All users</option>
          {(facets.data?.users || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        {userId && (
          <button className="btn-ghost text-sm" onClick={() => setUserId('')}>Clear</button>
        )}
        <div className="ml-auto text-xs text-muted">
          {q.isLoading ? 'Loading…' : `${recent.length} recent visits`}
        </div>
      </div>

      {!userId && (
        <div>
          <h3 className="text-sm font-semibold text-navy mb-2">Top Pages</h3>
          <div className="bg-white border border-panelDark rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Path</th>
                  <th className="text-right px-3 py-2">Views</th>
                  <th className="text-right px-3 py-2">Unique Users</th>
                </tr>
              </thead>
              <tbody>
                {topPages.map(p => (
                  <tr key={p.path} className="border-t border-panel hover:bg-panel/60">
                    <td className="px-3 py-1.5 font-mono text-xs">{p.path}</td>
                    <td className="px-3 py-1.5 text-right text-xs">{p.views}</td>
                    <td className="px-3 py-1.5 text-right text-xs">{p.unique_users}</td>
                  </tr>
                ))}
                {!q.isLoading && topPages.length === 0 && (
                  <tr><td colSpan={3} className="text-center text-muted py-6">No page views recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-navy mb-2">Recent Visits</h3>
        <div className="bg-white border border-panelDark rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Path</th>
              </tr>
            </thead>
            <tbody>
              {recent.map(r => (
                <tr key={r.id} className="border-t border-panel hover:bg-panel/60">
                  <td className="px-3 py-1.5 text-xs text-muted whitespace-nowrap">{fmtDt(r.at)}</td>
                  <td className="px-3 py-1.5">
                    <div className="font-medium text-navy">{r.user_name || r.user_email || <span className="text-muted italic">—</span>}</div>
                    {r.user_name && <div className="text-[11px] text-muted">{r.user_email}</div>}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-muted">{r.path}</td>
                </tr>
              ))}
              {!q.isLoading && recent.length === 0 && (
                <tr><td colSpan={3} className="text-center text-muted py-6">No visits recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ActivityTab() {
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');

  const facets = useQuery({
    queryKey: ['audit-facets'],
    queryFn: () => api.get('/admin/audit-log/facets').then(r => r.data.data),
  });

  const q = useQuery({
    queryKey: ['audit-log', 'activity', userId, action],
    queryFn: () => {
      const params = { limit: 500 };
      if (userId) params.user_id = userId;
      if (action) params.action = action;
      return api.get('/admin/audit-log', { params }).then(r => r.data.data);
    },
  });
  const rows = q.data || [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <select className="input w-auto" value={userId} onChange={e => setUserId(e.target.value)}>
          <option value="">All users</option>
          {(facets.data?.users || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select className="input w-auto" value={action} onChange={e => setAction(e.target.value)}>
          <option value="">All actions</option>
          {(facets.data?.actions || []).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {(userId || action) && (
          <button className="btn-ghost text-sm" onClick={() => { setUserId(''); setAction(''); }}>Clear</button>
        )}
        <div className="ml-auto text-xs text-muted">
          {q.isLoading ? 'Loading…' : `${rows.length} events`}
        </div>
      </div>

      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Entity</th>
              <th className="text-left px-3 py-2">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5 text-xs text-muted whitespace-nowrap">{fmtDt(r.at)}</td>
                <td className="px-3 py-1.5">
                  <div className="font-medium text-navy">{r.user_name || r.user_email || <span className="text-muted italic">anon</span>}</div>
                  {r.user_name && <div className="text-[11px] text-muted">{r.user_email}</div>}
                </td>
                <td className="px-3 py-1.5"><ActionChip action={r.action} /></td>
                <td className="px-3 py-1.5 text-xs text-muted">
                  {r.entity}{r.entity_id != null ? ` #${r.entity_id}` : ''}
                </td>
                <td className="px-3 py-1.5 text-xs text-muted truncate max-w-xs">
                  {r.after_state ? JSON.stringify(r.after_state).slice(0, 100) : '—'}
                </td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted py-6">No events found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
